import { useMemo } from 'react'
// I used the old 'react-query' library to make this hook compatible with src/loaders/carrier.ts,
// so that all carrier queries are using the same cache, and we don't repeat requests.
// Later we need to migrate all carrier queries to the new version at once.
import { useQueries } from 'react-query'

import carrierAPI from '@api/carrier'
import carrierUtils from '@lib/carrier'
import { UseCarrierLoaderParams } from '@loaders/carrier'

interface Props {
  connections: (Connection | null | undefined)[]
  currency: Currency
  locale: Locale
}

const useCarrierQueries = ({ connections, currency, locale }: Props) => {
  const codes = useMemo(() => {
    const filtered = connections.filter(Boolean) as Connection[]

    return [...new Set(filtered.map(carrierUtils.getCarrierCodes).flat())]
  }, [connections])

  const queries = useQueries(
    codes.map(code => {
      const props: UseCarrierLoaderParams = {
        marketingCarrierCode: code,
        params: { currency, locale },
      }

      return {
        queryKey: ['carrier', props],
        queryFn: () => carrierAPI.load(props.marketingCarrierCode!, props.params),
        enabled: !!code,
        cacheTime: 10 * 60 * 1000,
        staleTime: 10 * 60 * 1000,
      }
    }),
  )

  const isLoading = useMemo(() => queries.some(({ isLoading }) => isLoading), [queries])
  const data = useMemo(() => queries.map(({ data }) => data).filter(Boolean) as MarketingCarrier[], [queries])

  return {
    isLoading,
    data,
  }
}

export default useCarrierQueries
